import dayjs from 'dayjs';
import { naturalSort } from 'utils';
import { IEnhancedTableHeadCell, IFeedback, IZone } from '../models';

export const usersTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'id',
    label: 'Id',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'code',
    label: 'Employee Code',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'role',
    label: 'Role',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    disablePadding: false,
    sortable: false,
  },
];

export const feedbacksTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'date',
    label: 'Date Submitted',
    align: 'left',
    disablePadding: false,
    sortable: true,
    sortFunction(a: IFeedback, b: IFeedback): number {
      const bVal = dayjs(b.created_at);
      const aVal = dayjs(a.created_at);

      if (bVal.isBefore(aVal)) {
        return -1;
      }
      if (bVal.isAfter(aVal)) {
        return 1;
      }

      return 0;
    },
    width: 80,
  },
  {
    id: 'zone',
    label: 'Zone #',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
  {
    id: 'zone_name',
    label: 'Zone name',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
  {
    id: 'building_name',
    label: 'Building name',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 100,
  },
  {
    id: 'email',
    label: 'Email',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 260,
  },
  {
    id: 'phone',
    label: 'Phone',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 140,
  },
  {
    id: 'message',
    label: 'Issue',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 200,
  },
  {
    id: 'rating',
    label: 'Rating',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 120,
  },
];

export const decalsTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'code',
    label: 'Zone #',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'buildingAddress',
    label: 'Building Address',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'name',
    label: 'Zone Location',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'decalProof',
    label: 'Decal Proof',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'logoFile',
    label: 'Logo File',
    align: 'left',
    disablePadding: false,
    sortable: false,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    disablePadding: false,
    sortable: false,
  },
];

export const employeesTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'id',
    label: 'Employee Id',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'code',
    label: 'Employee Code',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'email',
    label: 'Email',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'phone',
    label: 'Phone',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    disablePadding: false,
    sortable: false,
  },
];

export const zonesZonesTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'Tier',
    label: 'Tier',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'zone',
    label: 'Zone #',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'zoneLocation',
    label: 'Zone Location',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'floor',
    label: 'Floor',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'rating',
    label: 'Rating',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'serviceItems',
    label: 'Service Items',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'lastServicedBy',
    label: 'Last Serviced By',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    disablePadding: false,
    sortable: false,
  },
];

const floorSortCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

// Prefer the displayed `floorInfo` so sort order matches what the user sees;
// fall back to the underlying floor record / id when older zones lack it.
const getZoneFloorSortValue = (zone: IZone): string =>
  String(zone.floorInfo ?? zone.floor?.floor ?? zone.building_floor_id ?? '').trim();

export const zonesTableTabsTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'decal',
    label: 'Decal',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 40,
  },
  {
    id: 'quantity',
    label: 'Quantity',
    align: 'center',
    disablePadding: false,
    sortable: false,
    width: 40,
  },
  {
    id: 'code',
    label: 'Zone #',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 100,
  },
  {
    id: 'floorInfo',
    label: 'Floor',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 80,
    sortFunction: (a: IZone, b: IZone) => {
      const aFloor = getZoneFloorSortValue(a);
      const bFloor = getZoneFloorSortValue(b);

      return floorSortCollator.compare(aFloor, bFloor);
    },
  },
  {
    id: 'name',
    label: 'Zone Name',
    align: 'left',
    disablePadding: false,
    sortable: true,
    sortFunction: (a: IZone, b: IZone) => naturalSort(a['name'], b['name']),
    checkboxFilter: true,
    width: 180,
  },
  {
    id: 'rating',
    label: 'Average Rating',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 140,
  },
  {
    id: 'serviceItemsText',
    label: 'Service Items',
    align: 'left',
    disablePadding: false,
    sortable: false,
    textFilter: true,
    width: 200,
  },
  {
    id: 'lastServicedBy',
    label: 'Last Serviced By',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 180,
  },
  {
    id: 'building',
    label: 'Building',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 180,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'left',
    disablePadding: false,
    sortable: true,
    width: 80,
  },
  {
    id: 'decalApproved',
    label: 'Decal Approved',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
  {
    id: 'decalApprovedAt',
    label: 'Decal Approved On',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    disablePadding: false,
    sortable: false,
    width: 80,
  },
];

export const zonesCartTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'code',
    label: 'Zone #',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'name',
    label: 'Zone Location',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'product',
    label: 'Type',
    align: 'left',
    disablePadding: false,
    sortable: true,
  },
  {
    id: 'cost',
    label: 'Monthly Price',
    align: 'right',
    disablePadding: false,
    sortable: true,
  },
];

export const onboardingReviewZonesTableColumns: IEnhancedTableHeadCell[] = [
  {
    id: 'product',
    label: 'Zone Type',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 120,
  },
  {
    id: 'zoneLocation',
    label: 'Zone Location',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 140,
  },
  {
    id: 'price',
    label: 'Price',
    align: 'left',
    disablePadding: false,
    sortable: false,
    width: 100,
  },
];
